import React from 'react';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { gettingStartedEntryPoint } from '@jira/spa-apps__servicedesk-getting-started-page/src/gettingStartedEntryPoint.entrypoint.tsx';
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point/src/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import {
	LegacyGettingStartedPageSkeleton,
	SolutionComposerGettingStartedPageSkeleton,
} from '@jira/platform__skeletons/src/ui/getting-started-page/GettingStartedPageSkeleton.tsx';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
import {
	getServiceDeskNavigationResources,
	getServicedeskPageContextResources,
} from '@jira/router-resources__service-desk-common/src/getServiceDeskSettingsResources.tsx';
import { servicedeskIssueTypesResource } from '@jira/router-resources__service-desk-issue-types/src/servicedeskIssueTypesResource.tsx';
import { servicedeskPortalGroupsResource } from '@jira/router-resources__service-desk-portal-groups/src/servicedeskPortalGroupsResource.tsx';
import { useIsRovoAiEnabledForJiraProduct } from '@jira/router-resources__rovo-entitlement/src/useIsRovoAiEnabledForJiraProduct.tsx';
import { servicedeskGettingStartedRoute } from '@jira/router-routes__servicedesk-getting-started-page-routes/src/servicedeskGettingStartedRoute.tsx';
import { useGettingStartedPageTemplateState } from '@jira/servicedesk__getting-started-page-common/src/useIsGettingStartedPageExcludedProject.tsx';
import { serviceProjectLayout } from '@jira/platform__layouts-service-project-layout/src/index.tsx';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Route } from '@atlassian/react-resource-router';

const gettingStartedPageEntryPoint = createPageEntryPoint({
	main: gettingStartedEntryPoint,
});

const LegacyGettingStartedRouteSkeleton = () => <LegacyGettingStartedPageSkeleton />;

const SolutionComposerEnabledGettingStartedRouteSkeleton = () => {
	const { isSolutionComposerTemplate, isInitialProjectTemplateLoading } =
		useGettingStartedPageTemplateState();
	const isRovoEnabledForJsm = useIsRovoAiEnabledForJiraProduct('JIRA_SERVICE_MANAGEMENT');

	if (isRovoEnabledForJsm && isInitialProjectTemplateLoading) {
		return <Spinner />;
	}

	if (isRovoEnabledForJsm && isSolutionComposerTemplate) {
		return <SolutionComposerGettingStartedPageSkeleton />;
	}

	return <LegacyGettingStartedPageSkeleton />;
};

const GettingStartedRouteSkeleton = componentWithFG(
	'solution_composer_master',
	SolutionComposerEnabledGettingStartedRouteSkeleton,
	LegacyGettingStartedRouteSkeleton,
);

export const servicedeskGettingStartedRouteEntry: Route = createEntry(
	servicedeskGettingStartedRoute,
	() => ({
		group: 'servicedesk',
		layout: serviceProjectLayout,
		perfMetricKey: 'servicedesk-getting-started-page-load',
		skeleton: GettingStartedRouteSkeleton,

		navigation: {
			menuId: MenuIdProjects,
		},

		resources: [
			...getServiceDeskNavigationResources(),
			...getServicedeskPageContextResources(),
			staticProjectSidebarNavigationResource,
			servicedeskIssueTypesResource,
			servicedeskPortalGroupsResource,
		],

		entryPoint() {
			if (fg('gsp_production_rollout')) {
				return gettingStartedPageEntryPoint;
			}
			if (fg('solution_composer_master')) {
				if (fg('jsm_solcom_use_blank_project_template')) {
					return gettingStartedPageEntryPoint;
				}
			}
			return undefined;
		},

		bifrost: {
			bifrostGroup: 'shell-only',
		},
	}),
);
